/** Welcome capture (#139): native form, shared capture suppression, accessible dialog. */
(() => {
  const CAPTURE_KEY = 'naturah:capture-submitted';
  const SUPPRESS_KEY = 'naturah:popup-suppressed-until';
  const CART_KEY = 'naturah:last-cart-add';
  const INTENT_KEY = 'naturah:popup-intent';
  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
  const CART_COOLDOWN = 60 * 1000;
  // Storage can be denied in private browsing. Keep this page usable in that case.
  const memory = new Map();
  function read(key) {
    try { return localStorage.getItem(key) || memory.get(key); }
    catch { return memory.get(key); }
  }
  function write(key, value) {
    memory.set(key, String(value));
    try { localStorage.setItem(key, String(value)); } catch { /* Page-local fallback. */ }
  }
  function suppress() { write(SUPPRESS_KEY, Date.now() + THIRTY_DAYS); }

  const popup = document.querySelector('[data-welcome-popup]');
  let invoker;
  let timer;
  let shown = false;
  let returnHandled = false;

  function close(remember = true, restoreFocus = true) {
    if (remember) suppress();
    if (!popup?.open) return;
    popup.close();
    document.body.classList.remove('welcome-popup-open');
    if (restoreFocus && invoker?.isConnected) invoker.focus();
  }

  // Delegation covers footer, homepage, blog and any other native customer capture,
  // including forms replaced by the theme editor. Runs even with popup_enabled off.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.elements.namedItem('form_type')?.value !== 'customer' || !form.checkValidity()) return;
    write(CAPTURE_KEY, '1');
    suppress();
    clearTimeout(timer);
    if (form.id === 'WelcomePopupForm') {
      // Preserve the intent for Shopify's validation-error round trip.
      write(INTENT_KEY, popup.querySelector('[data-popup-tags]').value);
    } else {
      close(false);
    }
  }, true);

  document.addEventListener('cart:item-added', () => {
    write(CART_KEY, Date.now());
    clearTimeout(timer);
    // CartDrawer handles the same event; do not steal its focus back.
    close(false, false);
    if (popup && !shown) timer = setTimeout(trigger, CART_COOLDOWN);
  });

  if (!popup) return;
  const intentStep = popup.querySelector('[data-popup-step="intent"]');
  const emailStep = popup.querySelector('[data-popup-step="email"]');
  const email = popup.querySelector('[type="email"]');
  const tags = popup.querySelector('[data-popup-tags]');
  const success = popup.querySelector('[data-popup-success]');
  const error = popup.querySelector('[data-popup-error]');
  const params = new URLSearchParams(location.search);
  // Shopify shares customer-form state across captures. A return aimed at a
  // different form must not open an offer through dwell, scroll or cart timers.
  // Latch this for the visit, even if another script later consumes the hash.
  const otherCaptureReturn = Boolean(location.hash && location.hash !== '#WelcomePopupForm' &&
    (success || error || params.has('capture-response')));
  const mobile = window.matchMedia('(max-width: 767px)');
  const routeRoot = window.Shopify?.routes?.root || '/';
  const path = location.pathname.startsWith(routeRoot)
    ? '/' + location.pathname.slice(routeRoot.length) : location.pathname;
  const excluded = /^\/(cart|checkouts?)(\/|$)/.test(path) || /^\/pages\/(wholesale|hello)\/?$/.test(path);

  function open() {
    invoker = document.activeElement;
    popup.showModal();
    document.body.classList.add('welcome-popup-open');
    shown = true;
    (success && !success.hidden ? success : !emailStep.hidden ? email : popup.querySelector('[data-popup-intent]')).focus();
  }

  function tryOpen() {
    clearTimeout(timer);
    if (excluded || otherCaptureReturn || shown || read(CAPTURE_KEY) || Number(read(SUPPRESS_KEY)) > Date.now()) return;
    const remaining = Number(read(CART_KEY)) + CART_COOLDOWN - Date.now();
    if (remaining > 0) {
      timer = setTimeout(tryOpen, remaining);
      return;
    }
    // Do not stack dialogs or interrupt someone typing in an inline capture.
    if (document.hidden || document.querySelector('[aria-modal="true"][aria-hidden="false"], dialog[open]') ||
        document.activeElement?.matches('input, textarea, select')) return;
    open();
  }

  function trigger() {
    tryOpen();
  }

  popup.querySelector('[data-popup-close]').addEventListener('click', () => close());
  popup.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  // The transparent dialog fills the viewport. Its panel is the visible card,
  // so mouse and touch both hit a real DOM backdrop, including in mobile WebKit.
  popup.addEventListener('click', (event) => {
    if (event.target === popup) close();
  });
  popup.querySelectorAll('[data-popup-intent]').forEach((button) => {
    button.addEventListener('click', () => {
      tags.value = `newsletter,popup,intent-${button.dataset.popupIntent}`;
      intentStep.hidden = true;
      emailStep.hidden = false;
      email.focus();
    });
  });
  popup.querySelector('[data-popup-back]').addEventListener('click', () => {
    emailStep.hidden = true;
    intentStep.hidden = false;
    popup.querySelector(`[data-popup-intent="${tags.value.split(',').includes('intent-gift') ? 'gift' : 'self'}"]`).focus();
  });
  // Match the cart drawer's wraparound keyboard behavior; showModal makes the
  // background inert as well, so pointer/assistive navigation cannot escape.
  popup.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...popup.querySelectorAll('button, input:not([type="hidden"]), a[href]')]
      .filter((element) => !element.disabled && element.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  });
  window.addEventListener('storage', () => {
    if (read(CAPTURE_KEY) || Number(read(SUPPRESS_KEY)) > Date.now()) close(false);
  });

  // All customer forms can receive Shopify's success state. Only this form's
  // own anchor may reopen its response, once; a footer signup must never do so.
  function handleReturn() {
    if (returnHandled || excluded || location.hash !== '#WelcomePopupForm' || (!success && !error)) return;
    returnHandled = true;
    clearTimeout(timer);
    intentStep.hidden = true;
    if (success) {
      write(CAPTURE_KEY, '1');
      suppress();
      success.hidden = false;
    } else {
      const savedTags = read(INTENT_KEY);
      const savedIntent = savedTags?.split(',').find((tag) => tag === 'intent-self' || tag === 'intent-gift');
      if (savedIntent) tags.value = `newsletter,popup,${savedIntent}`;
      emailStep.hidden = false;
      error.hidden = false;
    }
    history.replaceState(null, '', location.pathname + location.search);
    open();
  }
  handleReturn();
  window.addEventListener('hashchange', handleReturn);
  if (excluded || otherCaptureReturn || returnHandled) return;
  timer = setTimeout(trigger, mobile.matches ? 12000 : 8000);
  document.addEventListener('mouseout', (event) => {
    if (!mobile.matches && event.relatedTarget === null && event.clientY <= 0) trigger();
  });
  // The theme wrapper can own page scrolling (overflow-x:hidden also makes its
  // vertical overflow scrollable). Scroll does not bubble, so capture it from
  // the actual page scroller, excluding rails and the popup's own panel. An
  // anchor can scroll on load without user intent; only dwell applies there.
  document.addEventListener('scroll', (event) => {
    const scroller = event.target === document ? document.scrollingElement : event.target;
    if (!mobile.matches || location.hash || !(scroller instanceof HTMLElement) ||
        !scroller.contains(document.getElementById('main-content'))) return;
    const distance = scroller.scrollHeight - scroller.clientHeight;
    if (distance > 0 && scroller.scrollTop / distance >= 0.5) trigger();
  }, { capture: true, passive: true });
  // Test-only trigger shortcut; all eligibility and suppression rules still apply.
  if (params.get('popup') === 'now') trigger();
})();
